import React from "react";

interface Props {
  realPeoplePercentage: number;
  avatarUrl?: string;
  profileImage?:string;
}

const getRiskConfig = (value: number) => {
  if (value >= 75)
    return { label: "Excellent", color: "#22c55e" };
  if (value >= 60)
    return { label: "Good", color: "#eab308" };
  if (value >= 45)
    return { label: "Moderate Risk", color: "#f97316" };
  return { label: "High Risk", color: "#ef4444" };
};

export const AudienceHealthGauge: React.FC<Props> = ({
  realPeoplePercentage,
  avatarUrl,
}) => {
  const risk = getRiskConfig(realPeoplePercentage);

  const size = 220;
  const height = 200;
  const centerX = size / 2;
  const centerY = 170; // pushes arc downward like your design
  const radius = 90;
  const strokeWidth = 30;

  // Convert percentage → angle (-180 to 0)
  const angle = -180 + (realPeoplePercentage / 100) * 180;
  const rad = (angle * Math.PI) / 180;

  const pointerX = centerX + radius * Math.cos(rad);
  const pointerY = centerY + radius * Math.sin(rad);

  const avatarRadius = radius + 30;
  const avatarX = centerX + avatarRadius * Math.cos(rad);
  const avatarY = centerY + avatarRadius * Math.sin(rad);
  const GAP = 4;
  const segments = [
    { start: -180, end: -135, color: "#ef4444" },
    { start: -135, end: -90, color: "#f97316" },
    { start: -90, end: -45, color: "#eab308" },
    { start: -45, end: 0, color: "#22c55e" },
  ];

  const polarToCartesian = (
    cx: number,
    cy: number,
    r: number,
    angleDeg: number
  ) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad),
    };
  };

  const createArc = (start: number, end: number) => {
    const adjustedStart = start + GAP / 2;
    const adjustedEnd = end - GAP / 2;
    const startCoord = polarToCartesian(centerX, centerY, radius, adjustedStart);
    const endCoord = polarToCartesian(centerX, centerY, radius, adjustedEnd);
    return `
      M ${startCoord.x} ${startCoord.y}
      A ${radius} ${radius} 0 0 1 ${endCoord.x} ${endCoord.y}
    `;
  };

  return (
    <div className="audience-health-chart-container"
      style={{  position: "relative", margin:'0 auto'}}
    >
      <svg width={size} height={height} style={{marginTop:'-20px', marginLeft:'10px'}}>
        {segments.map((seg, i) => (
          <path
            key={i}
            d={createArc(seg.start, seg.end)}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
          />
        ))}
      </svg>

      {/* Pointer Dot */}
      <div
        style={{
          position: "absolute",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: risk.color,
          left: pointerX + 36,
          top: pointerY - 48,
          boxShadow: "0 0 0 4px white",
          transition: "all 0.4s ease",
        }}
      />

      {/* Avatar */}
      {avatarUrl && (
        <img
          src={avatarUrl}
          alt="profile"
          style={{
            position: "absolute",
            width: 56,
            height: 56,
            borderRadius: "50%",
            border: `4px solid ${risk.color}`,
            left: avatarX + 28,
            top: avatarY - 78,
            background: "#fff",
            boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
            transition: "all 0.4s ease",
          }}
        />
      )}

      {/* Center Label */}
      <div
        style={{
          position: "absolute",
          width: 250,
          bottom: 30,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: risk.color,
          }}
        >
          {realPeoplePercentage}%
        </div>

        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: risk.color,
          }}
        >
          {risk.label}
        </div>
      </div>
    </div>
  );
};
